import { FunctionComponent, ReactNode, useState } from "react";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

interface CollapsibleSettingsSectionProps {
	title: string;
	description: string;
	children: ReactNode;
}

/**
 * One collapsible card on the list settings page — the title/description row with the
 * chevron toggle that every CRM block on this page repeats verbatim. Extracted so a new
 * provider costs one usage instead of another ~45-line copy of the same markup.
 */
export const CollapsibleSettingsSection: FunctionComponent<CollapsibleSettingsSectionProps> = ({
	title,
	description,
	children,
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	return (
		<div
			className={classNames(
				"border p-6 mb-8 w-full items-start rounded-md border-1 border-border-light",
				"flex flex-col text-left gap-6",
				isOpen ? "bg-brand-white" : "bg-white"
			)}
		>
			<button
				className="w-full items-center group cursor-pointer flex flex-row text-left gap-6"
				type="button"
				onClick={() => setIsOpen((prev) => !prev)}
			>
				<div className="grow">
					<div className="text-smd text-text font-bold">{title}</div>
					<div className="text-ssm text-text-meta">{description}</div>
				</div>
				<div
					className={classNames(
						"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
						isOpen ? "border-border-light bg-white" : "border-transparent bg-background-light"
					)}
				>
					<Icon
						className="text-text-meta group-hover:text-white w-[12px] h-auto"
						icon={isOpen ? faChevronUp : faChevronDown}
					/>
				</div>
			</button>

			{isOpen && <div className="flex flex-col w-full">{children}</div>}
		</div>
	);
};
